import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DropdownContainer,
  DropdownIconContainer,
  DropdownInput,
  DropdownInputContainer,
  DropdownWrapperContainer
} from '../styled-components'
import { dropdownBoxStyleOptions, dropdownHeaderOptions } from '../constants'
import PropTypes from 'prop-types'
import DropdownHint from './dropdown-hint'
import DropdownClear from './dropdown-clear'
import DropdownLabel from './dropdown-label'
import { contextMenuSizeOptions } from '../../../organisms/context-menu/constants'
import ContextMenuComponent from '../../../organisms/context-menu'
import { isEmpty } from 'lodash'
import MenuHeader from './menu/menu-header'
import {
  TrailingDropDown,
  TrailingDropUp
} from '../../../atoms/icons/text-input'
import { iconSizeOptions } from '../../../atoms/icons/icons-sizes'

const DropdownSingle = ({
  boxStyle = dropdownBoxStyleOptions.MINIMAL,
  clearable,
  label,
  hint,
  disabled,
  readOnly,
  placeholder = 'Placeholder',
  customOnChange,
  noEmptyField = false,
  control = false,
  defaultValue = '',
  closeWhenSelected = false,

  radioProps = null,
  checkBoxProps = null,
  isLoading = false,

  options,
  menuProps = null,

  menuHeader = null,
  ...props
}) => {
  const safeMenuProps = menuProps || {}
  const [selectedItem, setSelectedItem] = useState(defaultValue)
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef()
  const inputRef = useRef()

  // tab states (outside of the component to avoid re-renders)
  const defaultTab = () => {
    if (menuHeader?.type === dropdownHeaderOptions.TABS) {
      return menuHeader?.tabOptions?.[0] ?? null
    }
    return null
  }

  const [selectedTab, setSelectedTab] = useState(defaultTab)

  const handleSelectTab = useCallback((event, tab) => {
    event.stopPropagation()
    setSelectedTab(tab)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleSetValue = (value) => {
    const currentValue = value[0]
    const uniqueValue = currentValue ?? null
    setSelectedItem(uniqueValue)
    customOnChange && customOnChange(uniqueValue)
  }

  const handleClickClear = (event) => {
    event.stopPropagation()
    setSelectedItem('')
  }

  const Arrow = () => {
    if (isOpen) {
      return <TrailingDropUp size={iconSizeOptions['ICON-S']} />
    }

    return <TrailingDropDown size={iconSizeOptions['ICON-S']} />
  }

  useEffect(() => {
    const handleEsc = (event) => event.key === 'Escape' && handleClose()
    const handleClick = (event) =>
      !wrapperRef.current?.contains(event.target) && handleClose()

    document.addEventListener('keydown', handleEsc)
    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  useEffect(() => {
    if (closeWhenSelected) {
      handleClose()
    }
  }, [selectedItem])

  const valueToShow = selectedItem?.title ?? ''
  const notEmptySelectedItem = !isEmpty(selectedItem)
  const showLabel = isOpen || notEmptySelectedItem
  const labelText = props.required ? `${label} *` : label
  const inputWidth = inputRef?.current?.offsetWidth

  return (
    <DropdownWrapperContainer ref={wrapperRef}>
      <ContextMenuComponent.Root
        control={safeMenuProps.control}
        size={contextMenuSizeOptions.DESKTOP}
        isLoading={isLoading}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        style={{ width: '100%' }}
        handleSelect={handleSetValue}
        defaultValue={[selectedItem]}
        isSingle
        noEmptyField={noEmptyField}
      >
        <DropdownContainer>
          <DropdownLabel
            label={label}
            labelText={labelText}
            showLabel={showLabel}
            disabled={disabled}
            readOnly={readOnly}
          />
          <ContextMenuComponent.Trigger>
            <DropdownInputContainer
              data-disabled={disabled}
              data-read-only={readOnly}
              data-open={isOpen}
              boxStyle={boxStyle}
            >
              <DropdownInput
                placeholder={showLabel ? placeholder : labelText}
                ref={inputRef}
                spellCheck="false"
                type="text"
                disabled={disabled}
                readOnly={true}
                value={valueToShow}
                {...props}
              />

              {notEmptySelectedItem && (
                <DropdownIconContainer>
                  <DropdownClear
                    clearable={clearable}
                    handleClickClear={handleClickClear}
                    disabled={disabled}
                    readOnly={readOnly}
                  />
                </DropdownIconContainer>
              )}

              <DropdownIconContainer>
                <Arrow />
              </DropdownIconContainer>
            </DropdownInputContainer>
            <ContextMenuComponent.List
              align={safeMenuProps.align}
              side={safeMenuProps.side}
              sideXOffset={safeMenuProps.sideXOffset}
              sideYOffset={safeMenuProps.sideYOffset}
              arrow={false}
              width={safeMenuProps.width ?? inputWidth}
            >
              <MenuHeader
                selectedTab={selectedTab}
                menuHeader={menuHeader}
                options={options}
                handleSelectTab={handleSelectTab}
              />
            </ContextMenuComponent.List>
          </ContextMenuComponent.Trigger>

          <DropdownHint hint={hint} />
        </DropdownContainer>
      </ContextMenuComponent.Root>
    </DropdownWrapperContainer>
  )
}

DropdownSingle.propTypes = {
  boxStyle: PropTypes.oneOf(Object.values(dropdownBoxStyleOptions)),
  clearable: PropTypes.bool,
  label: PropTypes.string,
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  noEmptyField: PropTypes.bool,

  placeholder: PropTypes.string,
  customOnChange: PropTypes.func,
  menuHeader: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(dropdownHeaderOptions)).isRequired,
    title: PropTypes.string,
    placeholder: PropTypes.string,
    tabOptions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        options: PropTypes.array.isRequired
      })
    )
  }),
  menuProps: PropTypes.shape({
    title: PropTypes.string,
    control: PropTypes.bool,
    align: PropTypes.string,
    side: PropTypes.string,
    sideXOffset: PropTypes.number,
    sideYOffset: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })
}

export default DropdownSingle
